/**
 * ChatMarkdown — Hybrider Markdown-Renderer für Chat-Nachrichten
 * Coaching-Antworten (kurz, emotional) → Plain-Text mit Absätzen
 * Assistenten-Antworten (Listen, Code, Tabellen) → Markdown-Rendering
 * Streaming-Nachrichten → Blinkender Cursor am Ende
 * Session-Links → Klickbare Session-Cards [SESSION:id|Titel]
 *
 * Anti-Flicker: React.memo verhindert Re-Renders von fertigen Nachrichten
 * während die Streaming-Nachricht aktualisiert wird.
 */
import { memo, useMemo } from "react";
import SessionLinkCard, { parseSessionLinks } from "./SessionLinkCard";

interface ChatMarkdownProps {
  text: string;
  isStreaming?: boolean;
  className?: string;
}

const SESSION_LINK_REGEX = /\[SESSION:[a-z0-9-]+\|[^\]]+\]/;

function hasMarkdownStructure(text: string): boolean {
  if (/^[\s]*[-*]\s+/m.test(text)) return true;
  if (/^[\s]*\d+\.\s+/m.test(text)) return true;
  if (/^#{1,6}\s+/m.test(text)) return true;
  if (/```/.test(text)) return true;
  if (/`[^`]+`/.test(text)) return true;
  if (/\*\*[^*]+\*\*/g.test(text)) return true;
  if (/\*[^*]+\*/g.test(text)) return true;
  if (/\[.+\]\(.+\)/.test(text)) return true;
  if (/^>\s+/m.test(text)) return true;
  if (/^---+$/m.test(text)) return true;
  return false;
}

function renderMarkdown(text: string): string {
  let html = text;

  // Remove [SESSION:...] links before markdown rendering (they'll be handled separately)
  html = html.replace(/\[SESSION:[a-z0-9-]+\|[^\]]+\]/g, "%%SESSION_PLACEHOLDER%%");

  html = html.replace(/```(\w*)\n?([\s\S]*?)```/g, (_match, _lang, code) => {
    return `<pre class="bg-[#f8f8f8]/50 rounded-lg p-3 my-2 overflow-x-auto"><code class="text-xs font-mono text-[#4a5e38]">${escapeHtml(code.trim())}</code></pre>`;
  });

  html = html.replace(/`([^`]+)`/g, '<code class="bg-[#f8f8f8]/50 px-1.5 py-0.5 rounded text-xs font-mono text-[#6b7f4e]">$1</code>');

  html = html.replace(/^### (.+)$/gm, '<h3 class="font-semibold text-sm mt-3 mb-1 text-[#2d3a1e]">$1</h3>');
  html = html.replace(/^## (.+)$/gm, '<h2 class="font-semibold text-sm mt-3 mb-1 text-[#2d3a1e]">$1</h2>');
  html = html.replace(/^# (.+)$/gm, '<h1 class="font-bold text-base mt-3 mb-1 text-[#2d3a1e]">$1</h1>');

  html = html.replace(/^>\s+(.+)$/gm, '<blockquote class="border-l-2 border-[#6b7f4e]/30 pl-3 my-2 text-[#4a5e38]/80 italic">$1</blockquote>');

  html = html.replace(/^---+$/gm, '<hr class="border-[#d4dcc4] my-3" />');

  html = html.replace(/\*\*([^*]+)\*\*/g, '<strong class="font-semibold text-[#6b7f4e]">$1</strong>');
  html = html.replace(/\*([^*]+)\*/g, '<em class="italic">$1</em>');

  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-[#6b7f4e] underline underline-offset-2 hover:text-[#b8963d]">$1</a>');

  html = html.replace(/^[\s]*[-*]\s+(.+)$/gm, '<li class="ml-4 list-disc text-sm leading-relaxed">$1</li>');
  html = html.replace(/((?:<li[^>]*>.*<\/li>\n?)+)/g, '<ul class="my-1.5 space-y-0.5">$1</ul>');

  html = html.replace(/^[\s]*\d+\.\s+(.+)$/gm, '<li class="ml-4 list-decimal text-sm leading-relaxed">$1</li>');
  html = html.replace(/((?:<li class="ml-4 list-decimal[^>]*>.*<\/li>\n?)+)/g, '<ol class="my-1.5 space-y-0.5">$1</ol>');

  html = html.replace(/\n\n/g, '</p><p class="mt-2">');
  html = html.replace(/\n/g, '<br />');
  html = `<p>${html}</p>`;

  return html;
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

/**
 * Inner component wrapped in React.memo — prevents re-rendering when
 * parent re-renders but text/isStreaming/className haven't changed.
 * This is critical during streaming: only the streaming message's
 * ChatMarkdown re-renders, while all previous messages stay stable.
 */
const ChatMarkdownInner = memo(function ChatMarkdownInner({
  text,
  isStreaming = false,
  className = "text-sm",
}: ChatMarkdownProps) {
  const hasSessionLinks = useMemo(() => SESSION_LINK_REGEX.test(text), [text]);
  const useMarkdown = useMemo(() => hasMarkdownStructure(text), [text]);

  // Memoize the rendered markdown HTML to avoid re-computing on every render
  const renderedHtml = useMemo(() => {
    if (!useMarkdown && !hasSessionLinks) return null;
    return renderMarkdown(text);
  }, [text, useMarkdown, hasSessionLinks]);

  // If there are session links, we need to render them as React components
  if (hasSessionLinks) {
    const segments = parseSessionLinks(text);

    return (
      <div className={`leading-relaxed ${className}`}>
        {segments.map((segment, i) => {
          if (segment.type === "session" && segment.sessionId && segment.title) {
            return (
              <SessionLinkCard
                key={`session-${i}-${segment.sessionId}`}
                sessionId={segment.sessionId}
                title={segment.title}
              />
            );
          }

          // Text segment — render as markdown or plain text
          const segText = segment.content;
          if (useMarkdown || hasMarkdownStructure(segText)) {
            const html = renderMarkdown(segText);
            return (
              <span
                key={`text-${i}`}
                className="chat-markdown"
                dangerouslySetInnerHTML={{ __html: html }}
              />
            );
          }

          return (
            <span key={`text-${i}`} className="whitespace-pre-wrap">
              {segText}
            </span>
          );
        })}
        {isStreaming && (
          <span className="inline-block w-0.5 h-4 bg-[#6b7f4e] ml-0.5 animate-pulse align-text-bottom" />
        )}
      </div>
    );
  }

  // No session links — original rendering logic
  if (useMarkdown && renderedHtml) {
    return (
      <div
        className={`leading-relaxed chat-markdown ${className}`}
        dangerouslySetInnerHTML={{ __html: renderedHtml + (isStreaming ? '<span class="inline-block w-0.5 h-4 bg-[#6b7f4e] ml-0.5 animate-pulse align-text-bottom" />' : '') }}
      />
    );
  }

  return (
    <p className={`leading-relaxed whitespace-pre-wrap ${className}`}>
      {text}
      {isStreaming && (
        <span className="inline-block w-0.5 h-4 bg-[#6b7f4e] ml-0.5 animate-pulse align-text-bottom" />
      )}
    </p>
  );
});

// Default export wraps the memoized component
export default ChatMarkdownInner;
